import classNames from 'classnames'
import { motion } from 'framer-motion'
import moment from 'moment'
import { useEffect, useRef } from 'react'

import { TRAFFIC_RESTORE } from '@apps-common/config/traffic'
import { useIntersection } from '@apps-common/hooks/useIntersection'
import { useSignalStrengthIcon } from '@apps-common/hooks/useSignalStrengthIcon'
import { IUnitTrafficModel } from '@apps-common/types/portal/unit'
import { calcSignalStrength } from '@apps-common/utils/helpers'

export const EventItemCardSkeleton = () => (
  <div className='relative overflow-hidden w-full bg-skeleton border border-border rounded-[30px] px-6 py-3 flex flex-col items-between gap-[8px] justify-start animate-pulse'>
    <div className='flex items-center justify-between gap-[5px] pt-[20px]'>
      <div className='flex'>
        <div className='grow bg-skeleton px-[15px] py-[5px] rounded-xl w-[150px] h-[31px]' />
      </div>
      <div className='flex items-center justify-center'>
        <div className='flex items-center justify-center mx-[3px] px-[7px] bg-skeleton rounded-full w-[44px] h-[22px]' />
        <div className='w-[25px] h-[25px] rounded-full flex items-center justify-center bg-skeleton' />
      </div>
    </div>
    <div>
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-[3px]'>
        <div className='bg-skeleton pl-[10px] pr-[3px] py-[3px] rounded-xl flex items-center justify-end h-[24px]'>
          <div className='bg-skeleton-hi w-[39px] h-[18px] rounded-lg' />
        </div>
        <div className='bg-skeleton pl-[10px] pr-[3px] py-[3px] rounded-xl flex items-center justify-end h-[24px]'>
          <div className='bg-skeleton-hi w-[39px] h-[18px] rounded-lg' />
        </div>
        <div className='bg-skeleton pl-[10px] pr-[3px] py-[3px] rounded-xl flex items-center justify-end h-[24px]'>
          <div className='bg-skeleton-hi w-[39px] h-[18px] rounded-lg' />
        </div>
        <div className='bg-skeleton pl-[10px] pr-[3px] py-[3px] rounded-xl flex items-center justify-end h-[24px]'>
          <div className='bg-skeleton-hi w-[39px] h-[18px] rounded-lg' />
        </div>
      </div>
    </div>
  </div>
)

interface IProps {
  event: IUnitTrafficModel
  interactionEvent?: IUnitTrafficModel
  containerClasses?: string
  innerClasses?: string
}

export const EventItemCard: React.FC<IProps> = ({ event, interactionEvent, containerClasses, innerClasses }) => {
  const { eventType, description, altDescription, eventCode, account, zone, partition, signalStrength, timeStamp } = event
  const ref = useRef<any>(null)
  const isVisible = useIntersection(ref, '-300px')

  const color = eventType?.toLowerCase() === TRAFFIC_RESTORE ? 'bg-success text-on-success' : 'bg-error text-on-error'

  useEffect(() => {
    if (interactionEvent && interactionEvent === event && !isVisible && ref?.current) {
      ref?.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })
    }
  }, [interactionEvent])

  const signal = useSignalStrengthIcon(parseInt(signalStrength, 10))
  const calculatedSignalStrength = calcSignalStrength(parseInt(signalStrength, 10))

  return (
    <motion.div
      ref={ref}
      animate={
        interactionEvent
          ? {
              opacity: interactionEvent === event ? 1 : 0.45,
              scale: interactionEvent === event ? 1.075 : 1,
              borderWidth: interactionEvent === event ? '1px' : '0px',
              transition: {
                duration: 0.3,
                ease: 'easeInOut'
              }
            }
          : {
              opacity: 1,
              scale: 1,
              transition: {
                duration: 0.3,
                ease: 'easeInOut'
              }
            }
      }
      className={classNames(
        'relative overflow-hidden w-full bg-surface-2 text-text rounded-[30px] px-6 py-3 flex flex-col items-between gap-[8px] justify-start border border-border',
        containerClasses
      )}
    >
      <motion.div
        initial={false}
        animate={{
          opacity: !interactionEvent || interactionEvent === event ? 0 : 1,
          transition: {
            duration: 0.3,
            ease: 'easeInOut'
          }
        }}
        className='absolute top-0 left-0 bg-surface/20 w-full h-full z-[2]'
      />
      <div className='absolute top-0 right-0 bg-surface rounded-bl-[15px] pl-[10px] pr-[20px] text-[0.7em] jost ml-[5px] py-[3px]'>
        {moment(timeStamp).format('Do MMM YY T HH:mm:ss')}
      </div>
      <div className='flex items-center justify-between gap-[5px] pt-[12px]'>
        <div className='flex'>
          <div className='text-text-muted jost grow text-[0.9rem] py-[5px] pl-[10px] rounded-xl'>
            {event?.altDescription || event?.description || (event as any)?.eventDescription}
          </div>
        </div>
        <div className='flex items-center justify-center'>
          <div className='flex items-center justify-center gap-1 mx-[3px] px-[7px] bg-surface text-text rounded-full'>
            <div className=' text-text text-[1.3rem] pb-1'>{signal.Icon}</div>
            <div className='flex items-center justify-center text-[0.9rem] rounded-full'>{calculatedSignalStrength}</div>
          </div>
          <div className={classNames('jost-bold w-[25px] h-[25px] rounded-full flex items-center justify-center', color)}>{eventType}</div>
        </div>
      </div>
      <div className='jost-light text-[0.9em] leading-[1em]'>
        <div className='grid grid-cols-2 lg:grid-cols-4 gap-2 jost-light text-[0.85em]'>
          <div className={classNames('bg-surface px-2 py-1 rounded-xl flex items-center justify-between grow', innerClasses)}>
            Acc#
            <div className='text-text px-[5px] rounded-lg jost-medium'>{event?.account || (event as any)?.accountCode}</div>
          </div>
          <div className={classNames('bg-surface px-2 py-1 rounded-xl flex items-center justify-between grow', innerClasses)}>
            Event
            <div className='text-text px-[5px] rounded-lg jost-medium'>{eventCode}</div>
          </div>
          <div className={classNames('bg-surface px-2 py-1 rounded-xl flex items-center justify-between grow', innerClasses)}>
            Zone <div className='text-text px-[5px] rounded-lg jost-medium'>{zone}</div>
          </div>
          <div className={classNames('bg-surface px-2 py-1 rounded-xl flex items-center justify-between grow', innerClasses)}>
            Partition
            <div className='text-text px-[5px] rounded-lg jost-medium'>{partition}</div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
